import { useMemo } from 'react'
import { REWARD_FOR_BASE_CABIN, FlightsClassTypes, RewardFareClass } from 'constants/flight'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { ONE_POINT_PROMO_DATE } from '../../OnePointPromo/promoContent'
import { CHARTER_CASH_CABINS } from 'lib/maldivesCharter/charterCabins'
import { getRewardPreviewRate } from 'lib/maldivesCharter/classicRewardPricing'
import { aggregateMaldivesCharterMonthTileRewardPricing } from '../MaldivesDirectCalendar/maldivesCharterMonthTileRewardPricing'
import { computeFareClassCellPrice } from '../MaldivesDirectCalendar/maldivesCharterTripOptionsTablePricing'
import type { CalendarItemBundleData, DateRange } from './types'
import type { MaldivesCabinHotelEntry } from './useMaldivesOffersPricing'
import type useCharterRewardSeatsIndicatorForDate from './useCharterRewardSeatsIndicatorForDate'
import type { CabinDateJourneys } from './charterRewardAvailabilityContext'

interface Params {
  dateRangesByMonth: Record<string, Array<DateRange>>
  journeysByCabinDate: Map<FlightsClassTypes, Map<string, CabinDateJourneys>>
  rewardIndicator: ReturnType<typeof useCharterRewardSeatsIndicatorForDate>
  cabinFilter: FlightsClassTypes | null
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  luxPlusEnabled: boolean
  seatEligiblePassengers: number
  rewardSeatsOnly: boolean
  rewardLockedForLogin: boolean
  priceMode?: 'total' | 'perPerson'
  totalPassengers?: number
  cashCabins?: ReadonlyArray<FlightsClassTypes>
}

interface MonthRewardState {
  monthOneWayPromos: Record<string, boolean>
  /** Hardcoded preview prices for logged-out/non-member users, per month. */
  rewardPreviewByMonth: Record<string, { points: number; cash: number }>
  /** Per-month seal visibility (at least one date has all reward legs available). */
  monthRewardSeals: Record<string, { showRewardSeal: boolean }>
  /** Cheapest real reward price per month from availability data (reward-seats-only mode). */
  monthRewardPricing: Record<string, { points: number; cash: number }>
}

/**
 * Derives per-month reward display state shared by desktop and mobile Maldives charter calendars:
 * preview pricing for non-members, reward seal visibility, and cheapest real reward pricing.
 */
export function useMaldivesCharterMonthRewardState({
  dateRangesByMonth,
  journeysByCabinDate,
  rewardIndicator,
  cabinFilter,
  hotelPriceByCabinAndDate,
  itemBundleDataByDate,
  luxPlusEnabled,
  seatEligiblePassengers,
  rewardSeatsOnly,
  rewardLockedForLogin,
  priceMode = 'total',
  totalPassengers = 0,
  cashCabins = CHARTER_CASH_CABINS,
}: Params): MonthRewardState {
  const cabinsToCheck = useMemo(() => cabinFilter ? [cabinFilter] : [...cashCabins], [cabinFilter, cashCabins])

  const promoEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsIncompleteCharterBundles)
  const monthOneWayPromos = useMemo(() => {
    if (!promoEnabled || rewardIndicator.isLoading || seatEligiblePassengers < 1 || !cabinsToCheck.includes(FlightsClassTypes.ECONOMY)) return {}
    return Object.fromEntries(Object.entries(dateRangesByMonth).map(([monthName, dateRanges]) => [
      monthName,
      dateRanges.some(({ checkIn, departureDate, oneWayBundle }) => {
        if (!oneWayBundle || departureDate !== ONE_POINT_PROMO_DATE) return false
        const journeys = journeysByCabinDate.get(FlightsClassTypes.ECONOMY)?.get(checkIn)
        if (!journeys?.departing || journeys.returning) return false
        const hotel = hotelPriceByCabinAndDate?.[FlightsClassTypes.ECONOMY]?.[checkIn]
        const price = computeFareClassCellPrice({
          fareClass: RewardFareClass.ECONOMY_REWARDS,
          hotelPrice: hotel?.hotelPrice ?? itemBundleDataByDate?.[checkIn]?.hotelPrice,
          departingFlight: journeys.departing,
          returningFlight: journeys.returning,
          allowIncompleteJourneys: true,
          seatEligiblePassengers,
          getLegResult: rewardIndicator.getLegResult,
        })
        return price.pointsTotal !== undefined && price.cashTotal !== undefined
      }),
    ]))
  }, [promoEnabled, rewardIndicator, seatEligiblePassengers, cabinsToCheck, dateRangesByMonth, journeysByCabinDate, hotelPriceByCabinAndDate, itemBundleDataByDate])

  const rewardPreviewByMonth = useMemo(() => {
    if (!rewardLockedForLogin) return {}
    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let lowestPreview: { points: number; cash: number } | undefined
      dateRanges.forEach(({ checkIn, departureDate, oneWayBundle }) => {
        for (const cabin of cabinsToCheck) {
          const h = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          const hotelPrice = h
            ? (luxPlusEnabled ? (h.hotelMemberPrice || h.hotelPrice || 0) : (h.hotelPrice || 0))
            : itemBundleDataByDate?.[checkIn]?.hotelPrice ?? 0
          const rate = getRewardPreviewRate({ cabin, oneWayBundle, departureDate })
          if (!rate || hotelPrice <= 0) continue

          const rawCash = hotelPrice + rate.cashPerPerson * seatEligiblePassengers
          if (!lowestPreview || rawCash < lowestPreview.cash) {
            lowestPreview = {
              points: rate.pointsPerPerson * seatEligiblePassengers,
              cash: rawCash,
            }
          }
        }
      })
      if (lowestPreview) {
        const divisor = priceMode === 'perPerson' && totalPassengers > 0 ? totalPassengers : 1
        result[monthName] = { points: lowestPreview.points / divisor, cash: lowestPreview.cash / divisor }
      }
    })
    return result
  }, [rewardLockedForLogin, cabinsToCheck, seatEligiblePassengers, dateRangesByMonth, hotelPriceByCabinAndDate, itemBundleDataByDate, luxPlusEnabled, priceMode, totalPassengers])

  const monthRewardSeals = useMemo(() => {
    const datesByMonth: Record<string, Array<{ dateKey: string; allLegsAvailable: boolean }>> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      datesByMonth[monthName] = dateRanges.map(({ checkIn, oneWayBundle }) => {
        const anyCabinAvailable = cabinsToCheck.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          const journeyKeys = [entry?.departing?.journeyKey, entry?.returning?.journeyKey].filter(
            (journeyKey): journeyKey is string => !!journeyKey,
          )
          const hasRequiredJourneys = oneWayBundle ? journeyKeys.length > 0 : journeyKeys.length === 2
          return hasRequiredJourneys && journeyKeys.every(journeyKey => rewardIndicator.getLegResult(journeyKey)?.available === true)
        })
        return { dateKey: checkIn, allLegsAvailable: anyCabinAvailable }
      })
    })
    return aggregateMaldivesCharterMonthTileRewardPricing({ datesByMonth })
  }, [dateRangesByMonth, journeysByCabinDate, rewardIndicator, cabinsToCheck])

  const monthRewardPricing = useMemo(() => {
    if (!rewardSeatsOnly) return {}
    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let lowestCash: number | undefined
      let correspondingPoints: number | undefined
      dateRanges.forEach(({ checkIn, oneWayBundle }) => {
        for (const cabin of cabinsToCheck) {
          const rewardFareClass = REWARD_FOR_BASE_CABIN[cabin]
          if (!rewardFareClass) continue

          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          if (!entry || (!oneWayBundle && (!entry.departing || !entry.returning)) ||
            (oneWayBundle && !entry.departing && !entry.returning) || !cabinHotel) continue

          const cellPrice = computeFareClassCellPrice({
            fareClass: rewardFareClass,
            hotelPrice: cabinHotel.hotelPrice,
            hotelMemberPrice: cabinHotel.hotelMemberPrice,
            departingFlight: entry.departing,
            returningFlight: entry.returning,
            allowIncompleteJourneys: oneWayBundle,
            seatEligiblePassengers,
            getLegResult: rewardIndicator.getLegResult,
          })
          const rawCash = luxPlusEnabled && cellPrice.cashMemberTotal !== undefined ? cellPrice.cashMemberTotal : cellPrice.cashTotal
          if (cellPrice.pointsTotal === undefined || rawCash === undefined) continue

          const divisor = priceMode === 'perPerson' && totalPassengers > 0 ? totalPassengers : 1
          const totalPoints = cellPrice.pointsTotal / divisor
          const totalCash = rawCash / divisor

          if (lowestCash === undefined || totalCash < lowestCash) {
            lowestCash = totalCash
            correspondingPoints = totalPoints
          }
        }
      })
      if (correspondingPoints !== undefined && lowestCash !== undefined) {
        result[monthName] = { points: correspondingPoints, cash: lowestCash }
      }
    })
    return result
  }, [rewardSeatsOnly, cabinsToCheck, dateRangesByMonth, journeysByCabinDate, seatEligiblePassengers, rewardIndicator.getLegResult, hotelPriceByCabinAndDate, luxPlusEnabled, priceMode, totalPassengers])

  return { rewardPreviewByMonth, monthRewardSeals, monthRewardPricing, monthOneWayPromos }
}
